import { TOKEN_TTL_MS } from '@/constants/auth';

// Decode a base64url JWT segment to a JSON object (UTF-8 safe).
function decodeSegment(segment) {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

// Decode a JWT's payload claims, or null when the token can't be parsed.
export function claimsFromToken(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    return decodeSegment(parts[1]);
  } catch {
    return null;
  }
}

// Backend returns scopes as a comma string of key:value pairs (e.g. "businessProfile:owner,...").
// Keep the value half ("owner") to match the CASL role derivation. Tolerates an array too.
export function parseScopes(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return String(raw)
    .split(',')
    .map((s) => s.split(':')[1] ?? s)
    .map((s) => s.trim())
    .filter(Boolean);
}

// The business profile a token is scoped to (its `businessProfileId` claim), as a string, or null.
export function businessProfileIdFromToken(token) {
  const id = claimsFromToken(token)?.businessProfileId;
  return id == null ? null : String(id);
}

// The parsed scope roles a token carries (its `businessProfileScopes` claim → ['owner', ...]).
export function scopesFromToken(token) {
  return parseScopes(claimsFromToken(token)?.businessProfileScopes);
}

// Derive a token's absolute expiry (ms epoch) from its JWT `exp` claim, so the frontend's notion
// of expiry tracks the backend exactly — the access-token TTL differs per environment (~5 min dev
// / ~60 min prod). Falls back to a fixed TTL from now when the token can't be parsed.
export function expiresAtFromToken(token) {
  const fallback = Date.now() + TOKEN_TTL_MS;
  if (!token || typeof token !== 'string') return fallback;
  const parts = token.split('.');
  if (parts.length < 2) return fallback;
  try {
    const payload = decodeSegment(parts[1]);
    if (typeof payload.exp === 'number') return payload.exp * 1000;
  } catch {
    /* malformed token — use the fallback */
  }
  return fallback;
}
